import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, CircularProgress, Alert,
  TextField, Snackbar, Tooltip, Chip, Collapse
} from '@mui/material';
import {
  // ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Gavel as GavelIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { StyledDialog, inputStyles } from './common';

const API_BASE = '/api';

// ===== ESTILOS GLASSMORPHISM (mismo patrón que AdminPlanes) =====
const getCardStyle = (isActive = false) => ({
  p: 2.5,
  borderRadius: 4,
  background: '#fff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  boxShadow: isActive
    ? '0 0 0 3px rgba(16, 185, 129, 0.4), 0 1px 3px rgba(0,0,0,0.08)'
    : '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
  border: isActive
    ? '2px solid #10b981'
    : '1px solid #e2e8f0',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 3,
    background: isActive
      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: isActive
      ? '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(16, 185, 129, 0.3)'
      : '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(100, 116, 139, 0.2)'
  },
  '&:active': {
    transform: 'translateY(-2px) scale(0.98)'
  }
});

const getIconBoxStyle = (gradient, shadowColor) => ({
  width: 44, height: 44, borderRadius: 3,
  background: gradient,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff',
  boxShadow: `0 8px 20px ${shadowColor}`,
  flexShrink: 0,
  '& svg': { fontSize: 22 }
});


const AdminPoliticas = () => {
  const userId = localStorage.getItem('user_id');

  // Estado principal
  const [loading, setLoading] = useState(true);
  const [politicas, setPoliticas] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Crear nueva versión
  const [createDialog, setCreateDialog] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newTexto, setNewTexto] = useState('');
  const [saving, setSaving] = useState(false);

  // Detalle de aceptaciones
  const [detailDialog, setDetailDialog] = useState({ open: false, politica: null });
  const [aceptaciones, setAceptaciones] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [textoExpandido, setTextoExpandido] = useState(false);

  // Eliminar
  const [deleteDialog, setDeleteDialog] = useState({ open: false, politica: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  // ===== CARGAR DATOS =====
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/politicas?user_id=${userId}`);
      if (!res.ok) {
        if (res.status === 403) {
          setError('No tienes permisos. Solo administradores de NARA Technology.');
          return;
        }
        throw new Error('Error al cargar políticas');
      }
      const data = await res.json();
      setPoliticas(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== CREAR NUEVA VERSIÓN =====
  const handleOpenCreate = () => {
    const current = politicas.find(p => p.activa);
    if (current) {
      const parts = current.version.split('.');
      const minor = parseInt(parts[1] || '0') + 1;
      setNewVersion(`${parts[0]}.${minor}`);
      setNewTexto(current.texto);
    } else {
      setNewVersion('1.0');
      setNewTexto('');
    }
    setCreateDialog(true);
  };

  const handleCreate = async () => {
    if (!newVersion.trim() || !newTexto.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/politicas?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: newVersion.trim(), texto: newTexto.trim() })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al crear');
      }
      setSnackbar({ open: true, message: `Versión ${newVersion} creada y activada`, severity: 'success' });
      setCreateDialog(false);
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ===== VER DETALLE DE ACEPTACIONES =====
  const handleViewDetail = async (politica) => {
    setDetailDialog({ open: true, politica });
    setAceptaciones([]);
    setTextoExpandido(false);
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_BASE}/admin/politicas/${politica.id}/aceptaciones?user_id=${userId}`);
      if (!res.ok) throw new Error('Error al cargar');
      const data = await res.json();
      setAceptaciones(data.aceptaciones);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoadingDetail(false);
    }
  };

  // ===== ELIMINAR =====
  const handleDelete = async () => {
    const politica = deleteDialog.politica;
    if (!politica) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/politicas/${politica.id}?user_id=${userId}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al eliminar');
      }
      setSnackbar({ open: true, message: `Versión ${politica.version} eliminada`, severity: 'success' });
      setDeleteDialog({ open: false, politica: null });
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // ===== RENDER: LOADING =====
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1' }} />
          <Typography sx={{ mt: 2, color: '#94a3b8' }}>Cargando políticas...</Typography>
        </Box>
      </Box>
    );
  }

  // ===== RENDER: ERROR =====
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const activa = politicas.find(p => p.activa);

  // ===== RENDER: PRINCIPAL =====
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>

      {/* HEADER */}
      <Box sx={{
        px: 3, pt: 3, pb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Box>
          <Typography sx={{ color: '#1e293b', fontSize: 24, fontWeight: 700 }}>
            Políticas de Privacidad
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            Control de versiones y evidencia de aceptación
          </Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={loadData} sx={{ color: '#64748b', '&:hover': { color: '#1e3a5f' } }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* BARRA DE ACCIONES */}
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
            color: '#fff', textTransform: 'none', fontWeight: 600,
            borderRadius: 2, px: 3,
            boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)',
            '&:hover': { boxShadow: '0 6px 20px rgba(124, 58, 237, 0.6)' }
          }}
        >
          Nueva Versión
        </Button>
        <Chip
          label={`${politicas.length} versión${politicas.length !== 1 ? 'es' : ''}`}
          sx={{ background: '#e2e8f0', color: '#475569', fontWeight: 600 }}
        />
      </Box>

      {/* GRID DE TARJETAS */}
      <Box sx={{
        px: 3, pb: 4,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2.5
      }}>
        {politicas.map((pol) => (
          <Box key={pol.id} sx={getCardStyle(pol.activa)}>
            {/* Badge ACTIVA */}
            {pol.activa && (
              <Box sx={{
                position: 'absolute', top: 10, right: 10,
                px: 1.2, py: 0.3, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                display: 'flex', alignItems: 'center', gap: 0.5
              }}>
                <CheckCircleIcon sx={{ fontSize: 12, color: '#fff' }} />
                <Typography sx={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>ACTIVA</Typography>
              </Box>
            )}

            {/* Header: icono + versión */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: pol.activa ? 1 : 0 }}>
              <Box sx={getIconBoxStyle(
                pol.activa
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                pol.activa ? 'rgba(16, 185, 129, 0.4)' : 'rgba(100, 116, 139, 0.3)'
              )}>
                <GavelIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                  Versión {pol.version}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {pol.activa ? 'En uso actualmente' : 'Inactiva'}
                </Typography>
              </Box>
            </Box>

            {/* Preview del texto */}
            <Box sx={{
              p: 1.5, borderRadius: 2, mb: 2,
              background: '#f8fafc', border: '1px solid #e2e8f0'
            }}>
              <Typography sx={{
                fontSize: 11, color: '#475569', lineHeight: 1.5,
                display: '-webkit-box', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}>
                {pol.texto}
              </Typography>
            </Box>

            {/* Metadata */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CalendarIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {pol.created_at ? new Date(pol.created_at).toLocaleDateString('es-CO', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  }) : 'Sin fecha'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <PeopleIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {pol.total_aceptaciones} paciente{pol.total_aceptaciones !== 1 ? 's' : ''} aceptaron
                </Typography>
              </Box>
            </Box>

            {/* Acciones */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => handleViewDetail(pol)}
                sx={{
                  flex: 1, textTransform: 'none', fontSize: 12, fontWeight: 600,
                  color: '#1e3a5f', background: '#eff6ff', borderRadius: 2,
                  '&:hover': { background: '#dbeafe' }
                }}
              >
                Ver detalle
              </Button>
              {!pol.activa && pol.total_aceptaciones === 0 && (
                <Tooltip title="Eliminar versión">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialog({ open: true, politica: pol })}
                    sx={{ color: '#ef4444', '&:hover': { background: '#fef2f2' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}

        {/* Estado vacío */}
        {politicas.length === 0 && (
          <Box sx={{
            gridColumn: '1 / -1', p: 5, textAlign: 'center',
            background: '#fff',
            borderRadius: 4, border: '1px solid #e2e8f0'
          }}>
            <GavelIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>
              No hay políticas configuradas
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              Crea la primera versión de tu política de privacidad
            </Typography>
          </Box>
        )}
      </Box>

      {/* ===== DIÁLOGO: CREAR NUEVA VERSIÓN ===== */}
      <StyledDialog
        open={createDialog}
        onClose={() => !saving && setCreateDialog(false)}
        title="Nueva Versión"
        icon={<GavelIcon />}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Al crear esta versión, la anterior se desactivará automáticamente.
            Los nuevos pacientes aceptarán esta versión.
          </Alert>

          <TextField
            sx={inputStyles}
            label="Versión"
            placeholder="Ej: 1.1, 2.0"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            sx={{ ...inputStyles, mt: 2 }}
            label="Texto de la política"
            placeholder="Escribe el texto completo del aviso de privacidad..."
            value={newTexto}
            onChange={(e) => setNewTexto(e.target.value)}
            fullWidth
            multiline
            rows={10}
            size="small"
          />

          <Typography sx={{ mt: 1, fontSize: 11, color: '#94a3b8' }}>
            Este texto se enviará por WhatsApp a cada paciente nuevo antes de atenderlo.
          </Typography>
        </StyledDialog.Body>

        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setCreateDialog(false)} disabled={saving} />
          <StyledDialog.PrimaryButton
            fullWidth
            onClick={handleCreate}
            loading={saving}
            disabled={!newVersion.trim() || !newTexto.trim()}
          >
            Crear y Activar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* ===== DIÁLOGO: DETALLE DE ACEPTACIONES ===== */}
      <StyledDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, politica: null })}
        title={`Versión ${detailDialog.politica?.version || ''}`}
        subtitle={detailDialog.politica?.activa ? 'Versión activa' : 'Versión inactiva'}
        icon={<GavelIcon />}
        chipLabel={detailDialog.politica?.activa ? 'Activa' : 'Inactiva'}
        maxWidth="sm"
      >
        <StyledDialog.Body>
          {/* Texto completo (expandible) */}
          <Box sx={{
            p: 2, borderRadius: 2, mb: 2,
            background: '#f8fafc', border: '1px solid #e2e8f0',
            cursor: 'pointer'
          }}
          onClick={() => setTextoExpandido(!textoExpandido)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: textoExpandido ? 1 : 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>
                Texto de la política
              </Typography>
              {textoExpandido ? <ExpandLessIcon sx={{ fontSize: 18, color: '#64748b' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} />}
            </Box>
            <Collapse in={textoExpandido}>
              <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {detailDialog.politica?.texto}
              </Typography>
            </Collapse>
            {!textoExpandido && (
              <Typography sx={{
                fontSize: 11, color: '#94a3b8', mt: 0.5,
                display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}>
                {detailDialog.politica?.texto}
              </Typography>
            )}
          </Box>

          {/* Info */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              icon={<CalendarIcon sx={{ fontSize: 14 }} />}
              label={detailDialog.politica?.created_at
                ? new Date(detailDialog.politica.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                : 'Sin fecha'
              }
              size="small"
              sx={{ background: '#eff6ff', color: '#1e3a5f', fontWeight: 600 }}
            />
            <Chip
              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
              label={`${aceptaciones.length} aceptaciones`}
              size="small"
              sx={{ background: '#f0fdf4', color: '#166534', fontWeight: 600 }}
            />
          </Box>

          {/* Lista de aceptaciones */}
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f', mb: 1 }}>
            Pacientes que aceptaron
          </Typography>

          {loadingDetail ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
            </Box>
          ) : aceptaciones.length === 0 ? (
            <Box sx={{
              p: 3, textAlign: 'center', borderRadius: 2,
              background: '#f8fafc', border: '1px solid #e2e8f0'
            }}>
              <PeopleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                Ningún paciente ha aceptado esta versión
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {aceptaciones.map((a, i) => (
                <Box key={i} sx={{
                  p: 1.5, borderRadius: 2,
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'background 0.2s',
                  '&:hover': { background: '#eff6ff' }
                }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 2,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <PersonIcon sx={{ fontSize: 16, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                      {a.nombre}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <PhoneIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>{a.numero}</Typography>
                      </Box>
                      {a.empresa && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <BusinessIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>{a.empresa}</Typography>
                        </Box>
                      )}
                      {a.fecha_aceptacion && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <CalendarIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                            {new Date(a.fecha_aceptacion).toLocaleDateString('es-CO', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </StyledDialog.Body>
        <StyledDialog.Actions>
          <StyledDialog.CancelButton onClick={() => setDetailDialog({ open: false, politica: null })}>
            Cerrar
          </StyledDialog.CancelButton>
        </StyledDialog.Actions>
      </StyledDialog>

      {/* ===== DIÁLOGO: CONFIRMAR ELIMINACIÓN ===== */}
      <StyledDialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, politica: null })}
        title="Eliminar Versión"
        icon={<WarningIcon />}
        variant="compact"
        maxWidth="xs"
      >
        <StyledDialog.Body compact>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            ¿Eliminar la versión <strong>{deleteDialog.politica?.version}</strong>?
            Esta acción no se puede deshacer.
          </Alert>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDeleteDialog({ open: false, politica: null })} disabled={deleting} />
          <Button
            fullWidth
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#fff', textTransform: 'none', fontWeight: 600,
              borderRadius: 2, py: 1.2,
              boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
              '&:hover': { boxShadow: '0 6px 20px rgba(239, 68, 68, 0.6)' }
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Eliminar'}
          </Button>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPoliticas;
